/**
 * Phase W.1 — AI Knowledge Center.
 *
 * Admin UI to upload PDF/DOCX/XLSX/TXT, paste manual Q&A, browse indexed
 * documents, search the KB, and try a RAG answer with citations.
 *
 * Route: /admin/ai-knowledge
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  BookOpen, Upload, Loader2, Trash2, FileText, Hash, Sparkles, Search,
  Plus, Tag, RefreshCw, X, Quote,
} from "lucide-react";

const KIND_BADGE = {
  pdf:    "bg-rose-100 text-rose-700 border-rose-200",
  docx:   "bg-blue-100 text-blue-700 border-blue-200",
  xlsx:   "bg-emerald-100 text-emerald-700 border-emerald-200",
  manual: "bg-amber-100 text-amber-700 border-amber-200",
  website:"bg-purple-100 text-purple-700 border-purple-200",
  txt:    "bg-gray-100 text-gray-700 border-gray-200",
  md:     "bg-gray-100 text-gray-700 border-gray-200",
};

export default function AIKnowledge() {
  const [stats, setStats] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyManual, setBusyManual] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showTest, setShowTest] = useState(false);
  // Manual entry form
  const [m, setM] = useState({ question: "", answer: "", category: "general", priority: 5, tags: "" });
  // Test playground
  const [testQ, setTestQ] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/kb/stats"),
        api.get("/admin/kb/documents"),
      ]);
      setStats(s.data);
      setDocs(d.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load KB");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("priority", "5");
      const r = await api.post("/admin/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Indexed ${r.data.chunks} chunks from ${r.data.title}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const onManualSave = async () => {
    if (!m.question || !m.answer) { toast.error("Question & answer required"); return; }
    setBusyManual(true);
    try {
      await api.post("/admin/kb/manual", {
        question: m.question, answer: m.answer, category: m.category,
        priority: Number(m.priority),
        tags: m.tags.split(",").map(x => x.trim()).filter(Boolean),
      });
      toast.success("Q&A added to knowledge base");
      setM({ question: "", answer: "", category: "general", priority: 5, tags: "" });
      setShowManual(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusyManual(false); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}" and its ${doc.chunk_count || 0} chunks?`)) return;
    try {
      await api.delete(`/admin/kb/documents/${doc.id}`);
      toast.success("Removed from knowledge base");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onTest = async () => {
    if (!testQ.trim()) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/kb/answer", { query: testQ.trim() });
      setTestResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test query failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-ai-knowledge">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-purple-600" /> AI Knowledge Center
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload PDF / DOCX / Excel / TXT, or paste Q&amp;A. AI will answer ONLY from this knowledge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTest(s => !s)} data-testid="kb-test-toggle"
            className="px-3 py-1.5 bg-white border border-purple-300 text-purple-700 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 hover:bg-purple-50">
            <Sparkles className="w-3.5 h-3.5" /> Test RAG
          </button>
          <button onClick={() => setShowManual(true)} data-testid="kb-add-manual-btn"
            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 rounded-lg text-xs font-bold inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Teach AI
          </button>
          <label data-testid="kb-upload-btn"
            className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload document
            <input type="file" hidden accept=".pdf,.docx,.xlsx,.xls,.txt,.md,.csv" onChange={onUpload} />
          </label>
          <button onClick={load} data-testid="kb-reload"
            className="p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" title="Reload">
            <RefreshCw className="w-3.5 h-3.5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kb-stats">
        <Stat label="Documents" value={stats?.document_count ?? "—"} Icon={FileText} tone="purple" />
        <Stat label="Indexed chunks" value={stats?.chunk_count ?? "—"} Icon={Hash} tone="blue" />
        <Stat label="Embedding model" value={stats?.embedding_model || "—"} Icon={Sparkles} tone="amber" small />
        <Stat label="Vector dim" value={stats?.embedding_dim ?? "—"} Icon={Tag} tone="green" />
      </div>

      {/* RAG test playground */}
      {showTest && (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 space-y-2" data-testid="kb-test-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-purple-900 text-sm flex items-center gap-1">
              <Sparkles className="w-4 h-4" /> RAG Playground · ask anything
            </h3>
            <button onClick={() => { setShowTest(false); setTestResult(null); }} className="p-1 hover:bg-purple-100 rounded">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <input
              value={testQ} onChange={(e) => setTestQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onTest()}
              placeholder="Customer ka sawal yahaan likho — e.g. 3BHK plan ka kya price hai?"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
              data-testid="kb-test-input"
            />
            <button onClick={onTest} disabled={testing || !testQ.trim()} data-testid="kb-test-submit"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg disabled:opacity-60 inline-flex items-center gap-1">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Test
            </button>
          </div>
          {testResult && (
            <div className="bg-white rounded-lg p-3 border border-purple-200 space-y-2" data-testid="kb-test-result">
              <div className="text-[10px] uppercase font-bold text-purple-700">AI answer
                {testResult.low_confidence && <span className="ml-1 text-rose-700">· low confidence (fallback)</span>}</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">{testResult.answer}</div>
              {testResult.chunks?.length > 0 && (
                <div className="border-t border-gray-100 pt-2">
                  <div className="text-[10px] uppercase font-bold text-gray-600 mb-1.5 flex items-center gap-1">
                    <Quote className="w-3 h-3" /> Knowledge used ({testResult.chunks.length})
                  </div>
                  <div className="space-y-1.5">
                    {testResult.chunks.map((c, i) => (
                      <div key={c.id} className="bg-gray-50 rounded-md p-2 text-xs border border-gray-100" data-testid={`kb-citation-${i}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-bold text-[#1a3c5e]">[{i + 1}] {c.title}</span>
                          <span className="text-gray-500 text-[10px]">
                            sim {c._similarity != null ? (c._similarity * 100).toFixed(0) + "%" : "—"}
                          </span>
                        </div>
                        <div className="text-gray-700 line-clamp-2">{c.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Documents table */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="kb-docs">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1a3c5e]">Indexed sources</h3>
          <span className="text-[11px] text-gray-500">{docs.length} document(s)</span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : docs.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 italic">
            Knowledge base is empty. Upload a price list or paste a Q&amp;A to get started — the AI will only answer from what you put here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Title</th>
                  <th className="text-left px-3 py-2 font-bold">Source</th>
                  <th className="text-right px-3 py-2 font-bold">Chunks</th>
                  <th className="text-left px-3 py-2 font-bold">Tags</th>
                  <th className="text-left px-3 py-2 font-bold">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`kb-doc-${d.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#1a3c5e]">{d.title}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-bold ${KIND_BADGE[d.kind] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                        {d.kind?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{d.chunk_count ?? "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(d.tags || []).slice(0, 4).map(t => (
                          <span key={t} className="px-1.5 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[10px]">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold ${
                        d.status === "indexed" ? "text-emerald-700" :
                        d.status === "failed" ? "text-rose-700" : "text-amber-700"
                      }`}>{d.status}</span>
                      {d.error && <div className="text-[9px] text-rose-600">{d.error}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => onDelete(d)} data-testid={`kb-delete-${d.id}`}
                        className="p-1 hover:bg-rose-50 rounded text-rose-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manual Q&A modal */}
      {showManual && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="kb-manual-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#1a3c5e]">Teach AI a Q&amp;A</h3>
              <button onClick={() => setShowManual(false)}><X className="w-4 h-4" /></button>
            </div>
            <label className="block text-[10px] uppercase font-bold text-gray-600">Question</label>
            <input data-testid="kb-manual-question" value={m.question} onChange={(e) => setM(s => ({ ...s, question: e.target.value }))}
              placeholder="e.g. Kya aap 3D walkthrough provide karte ho?"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <label className="block text-[10px] uppercase font-bold text-gray-600">Answer</label>
            <textarea data-testid="kb-manual-answer" value={m.answer} onChange={(e) => setM(s => ({ ...s, answer: e.target.value }))}
              placeholder="Verified company answer. AI will return this verbatim or paraphrased."
              rows={5} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] uppercase font-bold text-gray-600">Category</label>
                <input value={m.category} onChange={(e) => setM(s => ({ ...s, category: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-bold text-gray-600">Priority</label>
                <input type="number" min="1" max="10" value={m.priority} onChange={(e) => setM(s => ({ ...s, priority: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-bold text-gray-600">Tags</label>
                <input value={m.tags} onChange={(e) => setM(s => ({ ...s, tags: e.target.value }))}
                  placeholder="comma,separated"
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowManual(false)} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
              <button onClick={onManualSave} disabled={busyManual} data-testid="kb-manual-save"
                className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
                {busyManual ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Save to KB
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, Icon, tone = "purple", small = false }) {
  const tones = {
    purple: "from-purple-50 to-purple-100 border-purple-200 text-purple-900",
    blue:   "from-blue-50 to-blue-100 border-blue-200 text-blue-900",
    amber:  "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    green:  "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
  };
  return (
    <div className={`bg-gradient-to-br ${tones[tone]} rounded-2xl border p-4 flex items-center gap-3`}>
      <div className="bg-white/70 rounded-xl p-2.5"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-[10px] uppercase font-bold tracking-wide opacity-80">{label}</div>
        <div className={small ? "text-sm font-bold leading-tight" : "text-2xl font-extrabold leading-tight"}>{value}</div>
      </div>
    </div>
  );
}
